import React, { useState, useRef, useEffect } from 'react';
import { Skeleton } from 'antd';

interface OptimizedImageProps {
  src?: string;
  alt: string;
  width?: number | string;
  height?: number | string;
  className?: string;
  style?: React.CSSProperties;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  loading?: 'lazy' | 'eager';
  priority?: boolean; // Above-the-fold images için
  placeholder?: 'blur' | 'empty';
  fallback?: React.ReactNode;
  onError?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  'aria-label'?: string;
  role?: string;
}

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  width,
  height,
  className,
  style,
  objectFit = 'cover',
  loading = 'lazy',
  priority = false,
  placeholder = 'empty',
  fallback,
  onError,
  onLoad,
  'aria-label': ariaLabel,
  role,
  ...props
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalHeight !== 0) {
      setImageLoaded(true);
      setShowPlaceholder(false);
    }
  }, [src]);

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setImageLoaded(true);
    setShowPlaceholder(false);
    onLoad?.(e);
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setImageError(true);
    setShowPlaceholder(false);
    onError?.(e);
  };

  if (!src || imageError) {
    return (
      <>
        {fallback ?? (
          <div
            style={{
              width: width || '100%',
              height: height || '200px',
              background: 'linear-gradient(145deg, #f0f4f8 0%, #e2e8f0 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#94a3b8',
              gap: '6px',
              ...style
            }}
            role={role || 'img'}
            aria-label={ariaLabel || alt}
          >
            <span style={{ fontSize: '28px', lineHeight: 1 }}>{alt.charAt(0).toUpperCase()}</span>
            <span style={{ fontSize: '11px', fontWeight: 500 }}>Görsel yüklenemedi</span>
          </div>
        )}
      </>
    );
  }

  const imageStyle: React.CSSProperties = {
    width: width || '100%',
    height: height || 'auto',
    objectFit,
    display: 'block',
    ...style
  };

  return (
    <div
      style={{
        position: 'relative',
        width: width || '100%',
        height: height || 'auto',
        overflow: 'hidden'
      }}
      className={className}
    >
      {showPlaceholder && placeholder === 'blur' && (
        <Skeleton.Image
          active
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1
          }}
        />
      )}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        loading={loading}
        decoding="async"
        fetchPriority={priority ? 'high' : 'auto'}
        style={{
          ...imageStyle,
          opacity: imageLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
          position: showPlaceholder ? 'absolute' : 'relative',
          top: showPlaceholder ? 0 : 'auto',
          left: showPlaceholder ? 0 : 'auto',
          zIndex: showPlaceholder ? 2 : 1
        }}
        onLoad={handleLoad}
        onError={handleError}
        aria-label={ariaLabel || alt}
        role={role}
        {...props}
      />
    </div>
  );
};

export default OptimizedImage;
